import React from 'react';
import ReactDOM from 'react-dom';
import core, { App } from '@gin-betting/core';
import cssVars from 'css-vars-ponyfill';
import {
    forEach,
    isArray,
    isEmpty,
    isObjectLike,
    omit,
    reduce,
    transform,
    values,
} from 'lodash';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import {
    BettingSettingsKeys,
    BettingWidgets,
    classicIds,
    DARK_THEME,
    DEFAULT_BETTING_SETTINGS,
    DEFAULT_WIDGET_OPTIONS,
    Destination,
    DISABLE_PARTICIPANT_FEATURES_LOCALSTORAGE_KEY,
    esportIds,
    EventType,
    PublicRoutes,
    ROOT_CSS_SCORE,
    ROOT_STYLE_ID_ELEMENT,
    SlugPublicRoutes,
} from 'app-constants';
import {
    DEFAULT_FONT_MAPPING_BY_SUFFIX,
    DEFAULT_POSITION_PROPS,
    IBettingPalette,
    IBettingPinnedStreamPosition,
    IBettingSpacing,
} from 'styles';
import type {
    BettingSettingsOptions,
    BettingWidgetSettings,
    BettingWidgetsOptions,
    BreakpointSettings,
    EventPayloadsByType,
    IBettingAPI,
    IBettingEvents,
    SearchObj,
} from 'types';
import {
    checkValueInObj,
    checkWidgetContainerProvided,
    getPaletteCssVariables,
    getPinnedStreamPositionCssVariables,
    getSpacingCssVariables,
} from 'utils';
import adaptBettingOptions from 'utils/adaptBettingOptions';
import createRoutes from '../services/createRoutes';
import featureManager from '../services/feature-manager';
import { BettingConfigCtx } from './bettingConfigCtx';
import Root from './Root';

const {
    utils: { getBettingOptions, isInnerPage },
    services: { getHistory, BettingAPI: BettingAPICore, stringify },
    constants: { SportType },
} = core;

interface BettingCssVariablesMap {
    palette: ReturnType<typeof getPaletteCssVariables>;
    spacing: CssVariables;
    pinnedStreamPosition: CssVariables;
}

class BettingAPI
    extends BettingAPICore<EventPayloadsByType, BettingSettingsOptions>
    implements IBettingAPI {
    private pathname: string = getHistory().location.pathname;
    private widgets$: BettingWidgetSettings = new BehaviorSubject(
        DEFAULT_WIDGET_OPTIONS
    );

    public breakpoints: BreakpointSettings;
    private cssVariablesMap: Partial<BettingCssVariablesMap> = {};

    bettingEvents$: IBettingEvents = this.bettingEvents$;

    constructor() {
        super({
            bettingSettings$: new BehaviorSubject(DEFAULT_BETTING_SETTINGS),
            requiredCallbacks: ['getBanners'],
            fonts: DEFAULT_FONT_MAPPING_BY_SUFFIX,
        });
        this.setupPalette({});
        this.setupSpacing({});

        this.cssVariablesMap.pinnedStreamPosition = DEFAULT_POSITION_PROPS;

        window.__app.version = __VERSION__;
        window.__app.initialTitle = document.title;

        const {
            url: { staticEndpoint },
        } = getBettingOptions();

        if (!__DEV__ && staticEndpoint) {
            __webpack_public_path__ = `${staticEndpoint}/`;
        }
    }

    setupPalette(palette: Partial<IBettingPalette>): void {
        this.cssVariablesMap.palette = getPaletteCssVariables(palette);

        if (this.isInitialized) this.insertCssVariables();
    }

    setupSpacing(spacing: Partial<IBettingSpacing>): void {
        this.cssVariablesMap.spacing = getSpacingCssVariables(spacing);

        if (this.isInitialized) this.insertCssVariables();

        if (typeof spacing.topOffset !== 'undefined') {
            this.setupWidget(BettingWidgets.CATEGORIZER, {});
        }
    }

    setupPinnedStreamPosition(
        positionVars: Required<IBettingPinnedStreamPosition>
    ): void {
        this.cssVariablesMap.pinnedStreamPosition = getPinnedStreamPositionCssVariables(
            positionVars
        );

        if (this.isInitialized) this.insertCssVariables();
    }

    setupBreakpoints(breakpoints: BreakpointSettings): void {
        const validationError = validateCustomBreakpoints(breakpoints);

        if (validationError) {
            console.error(validationError);

            return;
        }

        this.breakpoints = breakpoints;
    }

    setupWidget<T extends BettingWidgets>(
        widget: T,
        options: BettingWidgetsOptions[T]
    ): void {
        const validationError = validateSetupWidget(widget, options);

        if (validationError) {
            console.error(validationError);

            return;
        }

        const widgets = this.widgets$.getValue();
        this.widgets$.next({
            ...widgets,
            [widget]: {
                ...DEFAULT_WIDGET_OPTIONS[widget],
                ...widgets[widget],
                ...options,
            },
        });
    }

    updateBettingOptions = (pathname: string): void => {
        const {
            url: { pagePath },
        } = getBettingOptions();

        const isBettingPage = pagePath === '';

        if (!isBettingPage) return;

        const { destination, sportIds } = getRedirectPayloadByPathname(
            pathname
        );

        // @ts-ignore bettingOptions is exist on window but we can't show it outside. We extend bettingOptions by renamed name props.
        window.bettingOptions = adaptBettingOptions({
            ...getBettingOptions(),
            selectedSportIds: sportIds,
        });

        this.bettingEvents$.dispatch(EventType.Redirect, {
            destination,
            link: '',
        });

        if (__DEV__) {
            try {
                localStorage.setItem(
                    'bettingOptions',
                    JSON.stringify({
                        ...omit(
                            JSON.parse(
                                localStorage.getItem('bettingOptions') || '{}'
                            ),
                            ['selectedSportIds', 'sportIds']
                        ),
                    })
                );
            } catch (e) {
                console.error(e);
            }
        }
    };

    setupFeatures(): void {
        const { features, participantFeatures } = getBettingOptions();

        if (!features) return;

        // NOTE: SSR realted; serializable "features" object is passed via network request
        if (typeof features !== 'function') {
            featureManager.setFeatures(features);
        } else {
            featureManager.setFeatures(window.bettingLoader.__getFeatures);
        }

        const transformedParticipantFeatures = transform(
            participantFeatures,
            (acc, { feature_name, feature_variant }) => {
                acc[feature_name] = feature_variant;
            },
            {} as { [key: string]: string }
        );

        if (
            !localStorage.getItem(DISABLE_PARTICIPANT_FEATURES_LOCALSTORAGE_KEY)
        ) {
            featureManager.setFeatures(transformedParticipantFeatures);
        }
    }

    init(): void {
        super.init();

        this.insertCssVariables();
        this.setupFeatures();

        const initPathname = getHistory().location.pathname;

        // TODO: fix for outright page
        if (
            initPathname.includes(App.Routes.OUTRIGHT) ||
            initPathname.includes(App.Routes.OUTRIGHTS_LIST)
        ) {
            this.updateBettingOptions(initPathname);
        }

        getHistory().listen(({ pathname }) => {
            if (pathname === this.pathname) return;

            this.pathname = pathname;

            // TODO: fix for outright page
            if (isInnerPage() && !pathname.includes(App.Routes.OUTRIGHT))
                return;

            this.updateBettingOptions(pathname);
        });
    }

    renderDOM = (rootEl: HTMLElement): void => {
        ReactDOM.render(
            <MainRoot rootEl={rootEl}>
                <BettingConfigCtx.Provider
                    value={{
                        subscribe: this.subscribeOnBettingSetting,
                        set: this.setBettingSetting,
                        widgets$: this.widgets$,
                        bettingEvents: this.bettingEvents$,
                    }}
                >
                    <Root breakpoints={this.breakpoints}>{createRoutes}</Root>
                </BettingConfigCtx.Provider>
            </MainRoot>,
            rootEl,
            () => {
                measurePageTimings();
            }
        );
    };

    private insertCssVariables() {
        const { palette, ...restVars } = this.cssVariablesMap;
        const [cssPalletInString, cssInString] = [
            { palette },
            restVars,
        ].map(varsMap =>
            reduce(
                varsMap,
                (acc, vars) =>
                    vars ? acc + convertCssVariablesToString(vars) : acc,
                ''
            )
        ) as Tuple<string, 2>;

        const rootStyleHTMLElement = BettingAPI.getRootHTMLStyleElement();
        rootStyleHTMLElement.innerHTML = `${ROOT_CSS_SCORE} {${cssInString}${cssPalletInString}}`;
        document.head.appendChild(rootStyleHTMLElement);

        cssVars();
    }

    private static getRootHTMLStyleElement(): HTMLStyleElement {
        const existed = document.getElementById(ROOT_STYLE_ID_ELEMENT);

        if (existed?.tagName === 'STYLE') return existed as HTMLStyleElement;

        const rootHTMLStyleElement = document.createElement('style');
        rootHTMLStyleElement.id = ROOT_STYLE_ID_ELEMENT;

        return rootHTMLStyleElement;
    }

    public enableAnalytics = (isEnableAnalytics: boolean): void => {
        this.setBettingSetting(
            BettingSettingsKeys.IS_ENABLE_ANALYTICS,
            isEnableAnalytics
        );
    };

    /**
     * @deprecated
     */
    public selectTheme = (): void => {
        this.setBettingSetting(BettingSettingsKeys.THEMES, DARK_THEME);
        console.warn(
            '"selectTheme" method is deprecated because "light" theme is obsoleted'
        );
    };

    public changeRoute = (path: string, query?: SearchObj): void => {
        if (!path) return;

        if (
            PublicRoutes.includes(path) ||
            SlugPublicRoutes.some(url => path.startsWith(url))
        ) {
            const history = getHistory();

            history.push({
                ...history.location,
                pathname: path,
                hash: window.location.hash,
                search: query ? stringify(query) : '',
            });
        } else {
            console.warn(
                'Invalid route!\n' +
                    ' Please consider using one of the public routes instead.\n' +
                    ' Note: routes that ends with slash require correct "slug" to be postfixed by!'
            );
            console.table(
                PublicRoutes.map((r, i) => ({
                    ['General routes']: r,
                    ['Slug routes']: SlugPublicRoutes[i] || '',
                }))
            );
        }
    };
}

function MainRoot({
    rootEl,
    children,
}: {
    rootEl: HTMLElement;
    children: JSX.Element;
}) {
    return rootEl.tagName === 'MAIN' ? children : <main>{children}</main>;
}

function convertCssVariablesToString(
    vars: CssVariables | ReturnType<typeof getPaletteCssVariables>
): string {
    return reduce(vars, (acc, value, prop) => `${acc}${prop}: ${value};`, '');
}

function measurePageTimings(delay: number = 1000): void {
    if (!window.__app || !window.__app.timings) return;

    setTimeout(() => {
        const times = window.timing.getTimes();
        const timeParams = [
            'firstPaintTime',
            'appcacheTime',
            'connectTime',
            'domReadyTime',
            'initDomTreeTime',
            'loadEventTime',
            'loadTime',
            'requestTime',
            'redirectTime',
        ];

        timeParams.forEach(param => {
            window.__app.timings[param] = times[param];
        });
    }, delay);
}

function validateCustomBreakpoints(
    breakpoints: BreakpointSettings
): Error | undefined {
    const keyForValidation: Array<keyof BreakpointSettings> = ['medium'];

    if (typeof breakpoints !== 'object') {
        return new Error(
            `Invalid argument. "Breakpoints" argument should be an object`
        );
    }

    const errors: string[] = [];

    forEach(breakpoints, (value, key) => {
        if (
            !keyForValidation.includes(key as keyof BreakpointSettings) ||
            typeof value !== 'number'
        )
            errors.push(`Invalid argument "${key}"`);
    });

    if (errors.length > 0) {
        return new Error(`\n${errors.join('\n')}`);
    }
}

function validateSetupWidget<T extends BettingWidgets>(
    widget: T,
    options: BettingWidgetsOptions[T]
): Error | undefined {
    if (!DEFAULT_WIDGET_OPTIONS[widget]) {
        return new Error(`Not supported widget "${widget}"`);
    }

    if (
        checkWidgetContainerProvided(options) &&
        options.containerElementId !== null &&
        typeof options.containerElementId !== 'string'
    ) {
        return new Error('Invalid argument "containerElementId"');
    }

    if (widget === BettingWidgets.BETSLIP_SIDEBAR) {
        return validateBetslipSidebarWidgetOptions(
            options as BettingWidgetsOptions[BettingWidgets.BETSLIP_SIDEBAR]
        );
    }

    if (widget === BettingWidgets.STREAM) {
        return validateStreamWidgetOptions(
            options as BettingWidgetsOptions[BettingWidgets.STREAM]
        );
    }

    if (widget === BettingWidgets.CATEGORIZER) {
        return validateCategorizerWidgetOptions(
            options as BettingWidgetsOptions[BettingWidgets.CATEGORIZER]
        );
    }
}

function validateBetslipSidebarWidgetOptions(
    options: BettingWidgetsOptions[BettingWidgets.BETSLIP_SIDEBAR]
): Error | undefined {
    if (
        options.canToggle !== undefined &&
        typeof options.canToggle !== 'boolean'
    ) {
        return new Error('Invalid argument "canToggle"');
    }

    if (options.isOpen !== undefined && typeof options.isOpen !== 'boolean') {
        return new Error('Invalid argument "isOpen"');
    }
}

function validateStreamWidgetOptions({
    scrollToFirstStream,
    disableMatchTracker,
    disableTheaterMode,
    pinMinimizedStreamOutOfView,
}: BettingWidgetsOptions[BettingWidgets.STREAM]): Error | undefined {
    if (
        scrollToFirstStream !== undefined &&
        typeof scrollToFirstStream !== 'boolean'
    ) {
        return new Error('Invalid argument "scrollToFirstStream"');
    }

    if (
        disableMatchTracker !== undefined &&
        typeof disableMatchTracker !== 'boolean' &&
        !Array.isArray(disableMatchTracker)
    ) {
        return new Error('Invalid argument "disableMatchTracker"');
    }

    if (
        disableTheaterMode !== undefined &&
        typeof disableTheaterMode !== 'boolean'
    ) {
        return new Error('Invalid argument "disableTheaterMode"');
    }

    if (
        pinMinimizedStreamOutOfView !== undefined &&
        typeof pinMinimizedStreamOutOfView !== 'boolean'
    ) {
        return new Error('Invalid argument "pinMinimizedStreamOutOfView"');
    }
}

function validateCategorizerWidgetOptions({
    sportsOrder,
}: BettingWidgetsOptions[BettingWidgets.CATEGORIZER]): Error | undefined {
    const CATEGORIZER_ERROR_LABEL = 'CATEGORIZER WIDGET SETUP' as const;

    if (
        sportsOrder !== undefined &&
        (isArray(sportsOrder) ||
            !isObjectLike(sportsOrder) ||
            isEmpty(sportsOrder))
    ) {
        return new Error(
            `${CATEGORIZER_ERROR_LABEL}\n\nInvalid argument 'sportsOrder'. 'sportsOrder' should be a object with fields.\n`
        );
    }

    const { invalidSportsOrderKeys, invalidSportsOrderValues } = reduce(
        sportsOrder,
        (
            acc: {
                invalidSportsOrderKeys: string[];
                invalidSportsOrderValues: string[];
            },
            value,
            sportType
        ) => {
            if (!checkValueInObj(SportType, sportType)) {
                acc.invalidSportsOrderKeys.push(sportType);
            }

            if (!isArray(value)) {
                acc.invalidSportsOrderValues.push(sportType);
            }

            return acc;
        },
        { invalidSportsOrderKeys: [], invalidSportsOrderValues: [] }
    );

    let errorMsg = '';

    if (invalidSportsOrderKeys.length) {
        errorMsg = `Fields: '${invalidSportsOrderKeys.join(
            "', '"
        )}' can't be used in 'sportsOrder'. Use only '${values(SportType).join(
            "', '"
        )}'.`;
    }

    if (invalidSportsOrderValues.length) {
        errorMsg = `${errorMsg}\nFields: '${invalidSportsOrderValues.join(
            "', '"
        )}' have invalid values. Use only 'Array' as a value.`;
    }

    if (errorMsg) {
        return new Error(`${CATEGORIZER_ERROR_LABEL}\n\n${errorMsg}\n`);
    }
}

function getRedirectPayloadByPathname(
    pathname: string
): {
    destination: Destination;
    sportIds: string[];
} {
    switch (true) {
        case pathname.includes(App.Routes.OUTRIGHTS_LIST):
        case pathname.includes(App.Routes.OUTRIGHT): {
            return {
                destination: Destination.Outright,
                sportIds: [...classicIds, ...esportIds],
            };
        }

        case pathname.includes(App.Routes.CLASSIC): {
            return {
                destination: Destination.Sport,
                sportIds: classicIds,
            };
        }

        case pathname.includes(App.Routes.ESPORT): {
            return {
                destination: Destination.Esport,
                sportIds: esportIds,
            };
        }

        case pathname === App.Routes.LIVE_LIST: {
            return {
                destination: Destination.Live,
                sportIds: [...classicIds, ...esportIds],
            };
        }

        case pathname.includes(App.Routes.RESULTS_LIST): {
            return {
                destination: Destination.Results,
                sportIds: [...classicIds, ...esportIds],
            };
        }

        default: {
            return {
                destination: Destination.MixSports,
                sportIds: [...classicIds, ...esportIds],
            };
        }
    }
}

export default BettingAPI;
